import {useEffect, useMemo, useRef, useState} from "react";
import Header from "./Header";
import Loading from "./Loading";
import {ChevronDown, Check} from "lucide-react";
import {fetchScoredManuscripts} from "../service/ManuscriptService";
import {useNavigate} from "react-router-dom";
import logo from "../images/Logo.png";
import {calculateOverallScore} from "../service/Operations";
import {GRID, LIST} from "../constants/Constants";

function ScoreHistory() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [manuscripts, setManuscripts] = useState([]);

    // NEW: type filter & dropdown state
    const [typeFilter, setTypeFilter] = useState("ALL"); // "ALL" | "CERTIFIED" | "QUICK"
    const [typeOpen, setTypeOpen] = useState(false);
    const typeRef = useRef(null);
    // DATE RANGE state
    const [dateFilter, setDateFilter] = useState("ALL"); // "ALL" | "7D" | "30D" | "CUSTOM"
    const [dateOpen, setDateOpen] = useState(false);
    const dateRef = useRef(null);

// Custom range values (YYYY-MM-DD)
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");

    const [view, setView] = useState(GRID);
    const [smallScreen, setSmallScreen] = useState(!window.innerWidth > 767);


// Label for the trigger
    const dateLabel = useMemo(() => {
        if (dateFilter === "ALL") return "All Time";
        if (dateFilter === "7D") return "Last 7 days";
        if (dateFilter === "30D") return "Last 30 days";
        if (dateFilter === "CUSTOM") {
            if (customStart && customEnd) {
                const fmt = (s) =>
                    new Date(s + "T00:00:00").toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                    });
                return `${fmt(customStart)} – ${fmt(customEnd)}`;
            }
            return "Custom…";
        }
        return "Date Range";
    }, [dateFilter, customStart, customEnd]);


    const TYPE_LABELS = {
        ALL: "All Types",
        CERTIFIED: "Certified",
        QUICK: "Quick Run",
    };

    useEffect(() => {
        setLoading(true);
        fetchScoredManuscripts(true)
            .then(response => {
                if (response) {
                    setView(response.view || LIST)
                    setManuscripts(response.manuscripts || []);
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    // Close the dropdown when clicking outside
    useEffect(() => {
        const onDocClick = (e) => {
            if (typeRef.current && !typeRef.current.contains(e.target)) setTypeOpen(false);
            if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false);
        };
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);


    // Keyboard support for the trigger
    const onTypeKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setTypeOpen((o) => !o);
        } else if (e.key === "Escape") {
            setTypeOpen(false);
        }
    };

    const onDateKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setDateOpen((o) => !o);
        } else if (e.key === "Escape") {
            setDateOpen(false);
        }
    };

    useEffect(() => {
        const updateRadius = () => {
            const width = window.innerWidth;
            if (width > 767) {
                setSmallScreen(false);
            } else {
                setSmallScreen(true);
            }
        };
        updateRadius();
        window.addEventListener('resize', updateRadius);
        return () => window.removeEventListener('resize', updateRadius);
    }, []);

    const filteredManuscripts = useMemo(() => {
        if (!manuscripts) return [];

        // 1) Type filter
        let list = manuscripts;
        if (typeFilter === "CERTIFIED") list = list.filter((m) => m.certified === true);
        else if (typeFilter === "QUICK") list = list.filter((m) => m.certified === false);

        // 2) Date filter
        if (dateFilter === "ALL") return list;

        const now = Date.now();
        const DAY = 24 * 60 * 60 * 1000;
        let start = null;
        let end = null;

        if (dateFilter === "7D") {
            start = now - 7 * DAY;
            end = now;
        } else if (dateFilter === "30D") {
            start = now - 30 * DAY;
            end = now;
        } else if (dateFilter === "CUSTOM") {
            if (customStart) start = new Date(customStart + "T00:00:00").getTime();
            if (customEnd) {
                const e = new Date(customEnd + "T00:00:00");
                e.setHours(23, 59, 59, 999); // inclusive end-of-day
                end = e.getTime();
            }
            // If reversed, swap
            if (start !== null && end !== null && start > end) {
                const t = start;
                start = end;
                end = t;
            }
            // If one bound missing, treat the other as the only constraint
        }

        return list.filter((m) => {
            const t = Number(m.scoredDate);
            if (Number.isNaN(t)) return false;
            if (start !== null && t < start) return false;
            if (end !== null && t > end) return false;
            return true;
        });
    }, [manuscripts, typeFilter, dateFilter, customStart, customEnd]);


    return (
        <div>
            <Header page="score-history"/>
            {loading ? (
                <Loading/>
            ) : (
                <div className="body">
                    <div className="heading">Score History</div>

                    <div className={"search-bar-wrapper dashboard-row"}>
                        {/* TYPE DROPDOWN */}
                        <div
                            ref={typeRef}
                            className={"submissions-input-half submissions-input-half-first"}
                            role="button"
                            tabIndex={0}
                            aria-haspopup="listbox"
                            aria-expanded={typeOpen}
                            onClick={() => setTypeOpen(o => !o)}        // make the whole box the trigger
                            onKeyDown={onTypeKeyDown}
                            style={{position: "relative"}}
                        >
                            <div
                                className="dropdown-trigger"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%"
                                }}
                            >
                                <span className={"dropdown-text"}>{TYPE_LABELS[typeFilter]}</span>
                                <span className="chevron" aria-hidden="true">
                              <ChevronDown className={`chevron-icon ${typeOpen ? "open" : ""}`}/>
                            </span>
                            </div>

                            <ul
                                role="listbox"
                                aria-label="Filter by type"
                                className={`dropdown-panel dropdown-panel-regular ${typeOpen ? "open" : ""}`}
                                data-open={typeOpen}
                                onClick={(e) => e.stopPropagation()}     // prevent panel clicks from toggling
                            >
                                {[
                                    {val: "ALL", label: "All Types"},
                                    {val: "CERTIFIED", label: "Certified"},
                                    {val: "QUICK", label: "Quick Run"},
                                ].map(opt => (
                                    <li
                                        key={opt.val}
                                        role="option"
                                        aria-selected={typeFilter === opt.val}
                                        onClick={() => {
                                            setTypeFilter(opt.val);
                                            setTypeOpen(false);
                                        }}
                                        style={{
                                            padding: "10px 12px",
                                            cursor: "pointer",
                                            background: typeFilter === opt.val ? "#f6f6f6" : "transparent",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between"
                                        }}
                                        className="dropdown-option"
                                    >
                                        <span>{opt.label}</span>
                                        {typeFilter === opt.val && <Check size={16}/>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div
                            ref={dateRef}
                            className={"submissions-input-half"}
                            role="button"
                            tabIndex={0}
                            aria-haspopup="listbox"
                            aria-expanded={dateOpen}
                            onClick={() => setDateOpen((o) => !o)}   // make the whole box the trigger
                            onKeyDown={onDateKeyDown}
                            style={{position: "relative"}}
                        >
                            <div
                                className="dropdown-trigger"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%"
                                }}
                            >
                                <span className={"dropdown-text"}>{dateLabel}</span>
                                <span className="chevron" aria-hidden="true">
                              <ChevronDown className={`chevron-icon ${dateOpen ? "open" : ""}`}/>
                            </span>
                            </div>

                            <ul
                                role="listbox"
                                aria-label="Filter by date range"
                                className={`dropdown-panel dropdown-panel-regular dropdown-panel-date ${dateOpen ? "open" : ""}`}
                                data-open={dateOpen}
                                onClick={(e) => e.stopPropagation()}   // clicks inside panel don't toggle it
                            >
                                {[
                                    {val: "ALL", label: "All Time"},
                                    {val: "7D", label: "Last 7 days"},
                                    {val: "30D", label: "Last 30 days"},
                                    {val: "CUSTOM", label: "Custom…"},
                                ].map((opt) => (
                                    <li
                                        key={opt.val}
                                        role="option"
                                        aria-selected={dateFilter === opt.val}
                                        onClick={() => {
                                            if (opt.val === "CUSTOM") {
                                                setDateFilter("CUSTOM");      // keep panel open for inputs
                                            } else {
                                                setDateFilter(opt.val);
                                                setDateOpen(false);
                                            }
                                        }}
                                        style={{
                                            padding: "10px 12px",
                                            cursor: "pointer",
                                            background: dateFilter === opt.val ? "#f6f6f6" : "transparent",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                        className="dropdown-option"
                                    >
                                        <span>{opt.label}</span>
                                        {dateFilter === opt.val && <Check size={16}/>}
                                    </li>
                                ))}

                                {dateFilter === "CUSTOM" && (
                                    <li
                                        className="dropdown-option"
                                        style={{
                                            padding: "12px",
                                            borderTop: "1px solid #eee",
                                            display: "block",
                                            background: "#fff",
                                        }}
                                    >
                                        <div style={{display: "grid", gap: 8}}>
                                            <label style={{fontSize: 12, color: "#555"}}>Start date</label>
                                            <input
                                                className={"calendar"}
                                                type="date"
                                                value={customStart}
                                                onChange={(e) => setCustomStart(e.target.value)}
                                                style={{padding: "8px", border: "1px solid #ddd", borderRadius: 6}}
                                            />
                                            <label style={{fontSize: 12, color: "#555", marginTop: 8}}>End date</label>
                                            <input
                                                type="date"
                                                value={customEnd}
                                                onChange={(e) => setCustomEnd(e.target.value)}
                                                style={{padding: "8px", border: "1px solid #ddd", borderRadius: 6}}
                                            />

                                            <div style={{
                                                display: "flex",
                                                gap: 8,
                                                marginTop: 12,
                                                justifyContent: "flex-end"
                                            }}>
                                                <div style={{margin: "auto"}}>
                                                    <button
                                                        className={"clear-button"}
                                                        type="button"
                                                        onClick={() => {
                                                            setCustomStart("");
                                                            setCustomEnd("");
                                                            // setDateFilter("ALL");
                                                            // setDateOpen(false);
                                                        }}
                                                        style={{
                                                            padding: "8px 10px",
                                                            border: "1px solid #ddd",
                                                            background: "#fff",
                                                            borderRadius: 6,
                                                            cursor: "pointer",
                                                            marginRight: "10px"
                                                        }}
                                                    >
                                                        Clear
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={!customStart && !customEnd}
                                                        onClick={() => setDateOpen(false)}
                                                        style={{
                                                            padding: "8px 10px",
                                                            border: "1px solid #0b5cff",
                                                            background: !customStart || !customEnd ? "#e6ecff" : "#0b5cff",
                                                            color: !customStart || !customEnd ? "#6b7aff" : "#fff",
                                                            borderRadius: 6,
                                                            cursor: !customStart && !customEnd ? "not-allowed" : "pointer",
                                                        }}
                                                    >
                                                        Apply
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                )}
                            </ul>
                        </div>

                    </div>
                    {filteredManuscripts && filteredManuscripts.length > 0 ? (
                            [...filteredManuscripts]
                                .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate)) // newest first
                                .map((manuscript) => (
                                    <div
                                        key={manuscript.id ?? manuscript.key}
                                        className={!smallScreen && view === GRID ? "upload dashboard-row fade-in score-history-grid-item" : "upload dashboard-row fade-in"}
                                        style={{border: '1px solid black', cursor: 'pointer'}}
                                        onClick={() => navigate("/submission?id=" + manuscript.id)}
                                    >
                                        <div className={"score-history-novelty-score"}>
                                            {manuscript.noveltyScore !== null && manuscript.noveltyScore !== undefined
                                                ? calculateOverallScore(manuscript)
                                                : "?"}
                                        </div>

                                        <div className={"score-history-entry-data"}>
                                            {new Date(manuscript.scoredDate).toLocaleDateString("en-US", {
                                                year: "numeric",
                                                month: "long",
                                                day: "numeric",
                                            })}
                                        </div>
                                        <div className={"score-history-entry-data"} style={{fontStyle: "italic"}}>
                                            {manuscript.title != null ? manuscript.title : manuscript.name.replace(".pdf", "")}
                                        </div>

                                        <div className={"score-history-entry-data"}>
                                            {manuscript.certified ? (
                                                <div>
                                                    <img src={logo} alt={"logo"} className={"score-history-small-logo"}/>
                                                    <span>Certified</span>
                                                </div>
                                            ) : (
                                                <div>Quick Run</div>
                                            )}
                                        </div>

                                        <table className={"score-history-table"}>
                                            <tbody>
                                            <tr className={"score-history-row"}>
                                                <td className={"score-history-td"}>Novelty: {manuscript.noveltyScore}</td>
                                                <td className={"score-history-td"}>Method: {manuscript.methodScore}</td>
                                                <td className={"score-history-td"}>Impact: {manuscript.impactScore}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                ))
                        )
                        :
                        <div>No manuscripts found</div>
                    }

                </div>
            )}
        </div>
    );
}

export default ScoreHistory;
